import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  NumberInput,
  Divider,
  Select,
  Tooltip,
} from "@mantine/core";
import { notifySuccess } from "../../utils/notify";
import {
  IconAdjustmentsHorizontal,
  IconAlertCircle,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { SalonContextType } from "../../types/context";
import {
  ClientServicePackageBalance,
  PackageReasonCategory,
  AdjustBankRequest,
} from "../../types/packages";
import { unitLabelForBasis } from "../../utils/quantityUtils";
import ReasonFields from "./ReasonFields";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";

interface AdjustBankModalProps {
  opened: boolean;
  onClose: () => void;
  salonId: string;
  /** The owned package (client_service_package) id. */
  cspId: string;
  /** The package's per-service banks; the modal asks which one to adjust. */
  services: ClientServicePackageBalance[];
  onSuccess: () => void | Promise<void>;
}

/**
 * Add or remove units from a service bank. The staffer first picks which service
 * bank to adjust (auto-selected when the package has only one), then enters a
 * signed delta (positive adds, negative removes) that the backend applies to
 * BOTH the bank's total and remaining. The input noun (services / units /
 * syringes) is driven by the selected service's price basis. Banks may be
 * fractional (e.g. 1.5 syringes), so decimals are allowed. Every adjustment
 * records an `adjust_bank` audit row with a required reason + note.
 */
export default function AdjustBankModal({
  opened,
  onClose,
  salonId,
  cspId,
  services,
  onSuccess,
}: AdjustBankModalProps) {
  const { isMobile } = useResponsive();
  const { adjustClientPackageBalance } = useApp();
  const { services: salonServices } = useSalon() as SalonContextType;

  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
    null,
  );
  const [delta, setDelta] = useState<number | "">("");
  const [category, setCategory] = useState<PackageReasonCategory | null>(null);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setDelta("");
      setCategory(null);
      setNote("");
      setError(null);
      setIsSubmitting(false);
      // Auto-select the sole bank; otherwise the staffer picks one first.
      setSelectedServiceId(
        services.length === 1 ? services[0].salon_service_id : null,
      );
    }
  }, [opened, services]);

  const selectedService =
    services.find((s) => s.salon_service_id === selectedServiceId) ?? null;

  // Unit noun from the selected service's price basis (syringes / units),
  // falling back to "services" for a per_service (flat) basis.
  const priceBasis = selectedService
    ? salonServices.find((s) => s.id === selectedService.salon_service_id)
        ?.price_basis
    : undefined;
  const unitNoun = unitLabelForBasis(priceBasis, 2) || "services";

  const deltaIsNumber = typeof delta === "number";
  const deltaNum = deltaIsNumber ? delta : NaN;

  // The NumberInput enforces ≤ 2 decimals (decimalScale); a zero delta is the
  // only invalid numeric input reachable here. The backend floors total/remaining
  // at 0, so over-removal surfaces as a server error rather than a client guard.
  const validationError =
    deltaIsNumber && deltaNum === 0 ? "Enter a non-zero amount." : null;

  const canSubmit =
    selectedService !== null &&
    deltaIsNumber &&
    deltaNum !== 0 &&
    validationError === null &&
    !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit || selectedService === null) return;
    setIsSubmitting(true);
    setError(null);
    const body: AdjustBankRequest = {
      quantity_delta: deltaNum,
      reason_category: category,
      note: note.trim(),
    };
    try {
      await adjustClientPackageBalance(
        salonId,
        cspId,
        selectedService.salon_service_id,
        body,
      );
      notifySuccess(`${selectedService.service_name} balance updated.`, {
        title: "Bank adjusted",
      });
      try {
        await Promise.resolve(onSuccess());
      } catch (e) {
        console.error("Error in adjust-bank success handler:", e);
      }
      onClose();
    } catch (err) {
      console.error("Error adjusting bank:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to adjust the bank. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600}>
          {`Adjust Bank${
            selectedService ? ` - ${selectedService.service_name}` : ""
          }`}
        </Text>
      }
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <BrandedLoadingOverlay visible={isSubmitting} />

      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        {services.length > 1 && (
          <Select
            label="Service"
            placeholder="Select a service to adjust"
            data={services.map((s) => ({
              value: s.salon_service_id,
              label: s.service_name,
            }))}
            value={selectedServiceId}
            onChange={setSelectedServiceId}
            required
            disabled={isSubmitting}
          />
        )}

        <NumberInput
          label={
            <Group
              gap={4}
              align="center"
              wrap="nowrap"
              component="span"
              style={{ display: "inline-flex", verticalAlign: "middle" }}
            >
              Add or remove {unitNoun}
              <Tooltip
                label="Use a positive number to add, or a negative number to remove."
                multiline
                w={240}
                withArrow
                events={{ hover: true, focus: true, touch: true }}
              >
                <IconInfoCircle
                  size={14}
                  style={{
                    cursor: "help",
                    color: "var(--mantine-color-dimmed)",
                  }}
                />
              </Tooltip>
            </Group>
          }
          placeholder="e.g. 2 to add, -1 to remove"
          value={delta}
          onChange={(value) => setDelta(typeof value === "number" ? value : "")}
          decimalScale={2}
          allowNegative
          withAsterisk
          disabled={isSubmitting || selectedService === null}
        />

        {validationError && (
          <Text size="sm" c="red">
            {validationError}
          </Text>
        )}

        <ReasonFields
          category={category}
          note={note}
          onCategoryChange={setCategory}
          onNoteChange={setNote}
          disabled={isSubmitting}
          categoryRequired={false}
          noteRequired={false}
        />

        <Divider />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit}
            leftSection={<IconAdjustmentsHorizontal size={18} />}
          >
            Save Adjustment
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
